import { useState } from "react";

type OrgUserOption = {
  id: number;
  username: string;
  email: string;
};

type InternalRecipientEmailsEditorProps = {
  emails: string[];
  orgUsers: OrgUserOption[];
  disabled?: boolean;
  onChange: (emails: string[]) => void;
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function normalizeEmail(raw: string): string | null {
  const trimmed = raw.trim().toLowerCase();
  if (!EMAIL_PATTERN.test(trimmed)) return null;
  return trimmed;
}

export function InternalRecipientEmailsEditor({
  emails,
  orgUsers,
  disabled = false,
  onChange,
}: InternalRecipientEmailsEditorProps) {
  const [inputValue, setInputValue] = useState("");

  const addEmail = (raw: string) => {
    const normalized = normalizeEmail(raw);
    if (!normalized || emails.includes(normalized)) return false;
    onChange([...emails, normalized]);
    return true;
  };

  const handleAdd = () => {
    if (addEmail(inputValue)) {
      setInputValue("");
    }
  };

  return (
    <div className="space-y-2">
      <p className="text-xs text-slate-500">
        Add one or more email addresses. Escalation emails go to every address
        listed here.
      </p>
      {emails.length > 0 ? (
        <ul className="flex flex-wrap gap-2">
          {emails.map((email) => (
            <li
              key={email}
              className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs text-slate-700"
            >
              <span>{email}</span>
              <button
                type="button"
                disabled={disabled}
                aria-label={`Remove ${email}`}
                className="rounded px-1 text-slate-400 hover:text-red-600 disabled:opacity-50"
                onClick={() => onChange(emails.filter((item) => item !== email))}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-amber-700">Add at least one email address.</p>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="email"
          value={inputValue}
          disabled={disabled}
          placeholder="name@company.com"
          onChange={(event) => setInputValue(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              handleAdd();
            }
          }}
          className="min-w-[14rem] flex-1 rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-sm"
        />
        <button
          type="button"
          disabled={disabled}
          onClick={handleAdd}
          className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          Add email
        </button>
      </div>
      {orgUsers.length > 0 ? (
        <label className="block text-sm">
          <span className="text-slate-600">Add from team member</span>
          <select
            className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-2.5 py-1.5"
            disabled={disabled}
            defaultValue=""
            onChange={(event) => {
              const userId = Number(event.target.value);
              event.target.value = "";
              if (!userId) return;
              const user = orgUsers.find((item) => item.id === userId);
              if (user?.email) addEmail(user.email);
            }}
          >
            <option value="">Choose team member…</option>
            {orgUsers.map((user) => (
              <option key={user.id} value={user.id}>
                {user.username || user.email}
              </option>
            ))}
          </select>
        </label>
      ) : null}
    </div>
  );
}
